import { flushSync } from 'react-dom';
import { ELK_FOR_LAYOUT_VERSION, LAYOUT_ANIMATION_DURATION } from './constants';
import { customOverridesDriftFromHeld, elkPositionsNearlyEqual } from './elkComparison';
import { animateNodesToPositions, snapDomNodesToPositions } from './elkFlipAnimation';
import { getManualPositions, manualPositionsDivergeFromElk, resolveLayoutAnimMs } from './elkPositionHelpers';

export function createElkCommitHelpers({
  isExplicitAnimatedLayout,
  endLayoutHold,
  scheduleLayoutEdgeRevealAfter,
  setElkLayoutResult,
  commitElk,
}) {
  const finishExplicitHold = () => {
    if (isExplicitAnimatedLayout) {
      endLayoutHold?.();
    }
  };

  const revealEdgesAfterExplicitTidyIfNeeded = () => {
    if (isExplicitAnimatedLayout) {
      scheduleLayoutEdgeRevealAfter(0);
    }
  };

  // For an explicit Tidy the reveal is scheduled right after the commit; commitElk uses
  // startTransition (low priority), so the reveal's rAF can paint BEFORE the ELK result
  // lands — one or two frames of legacy routing (the "old connections flash" on Tidy).
  // Commit synchronously in that case so revealed edges always have the fresh ELK paths.
  const commitElkBeforeReveal = (value) => {
    if (isExplicitAnimatedLayout) {
      flushSync(() => {
        setElkLayoutResult(value);
      });
    } else {
      commitElk(value);
    }
  };

  return { finishExplicitHold, revealEdgesAfterExplicitTidyIfNeeded, commitElkBeforeReveal };
}

/** Commit, snap, or FLIP-animate a computed ELK result into the flowchart. */
export function applyElkLayoutResult({
  result,
  layoutTasks,
  isCancelled,
  layoutVersionWhenStarted,
  layoutVersionRef,
  layoutRequestModeRef,
  initialElkSettlementRef,
  lastCommittedElkPositionsRef,
  customPositionsRef,
  clearCustomPositionsBeforeElkApplyRef,
  connectLayoutCenterRef,
  isExplicitAnimatedLayout,
  isInstantLayout,
  commitElk,
  commitElkBeforeReveal,
  revealEdgesAfterExplicitTidyIfNeeded,
  finishExplicitHold,
  startLayoutEdgeHiddenPhase,
  scheduleLayoutEdgeRevealAfter,
  setElkLayoutResult,
}) {
  if (!result || !result.positions) {
    if (!isCancelled() && layoutVersionWhenStarted === layoutVersionRef.current) {
      commitElk(null);
      revealEdgesAfterExplicitTidyIfNeeded();
      finishExplicitHold();
    }
    return;
  }

  const stamped = { ...result, [ELK_FOR_LAYOUT_VERSION]: layoutVersionWhenStarted };

  if (initialElkSettlementRef.current) {
    initialElkSettlementRef.current = false;
    if (layoutRequestModeRef) layoutRequestModeRef.current = null;
    commitElkBeforeReveal(stamped);
    revealEdgesAfterExplicitTidyIfNeeded();
    finishExplicitHold();
    return;
  }

  const held = lastCommittedElkPositionsRef.current;
  const nextPos = result.positions;
  const custom = customPositionsRef?.current;
  const displayDrift = customOverridesDriftFromHeld(custom, held, layoutTasks);
  const manualPositions = getManualPositions(layoutTasks, custom);
  const manualDrift = manualPositionsDivergeFromElk(manualPositions, nextPos, layoutTasks);
  if (
    !displayDrift &&
    !manualDrift &&
    held &&
    Object.keys(held).length > 0 &&
    elkPositionsNearlyEqual(held, nextPos, layoutTasks, 1)
  ) {
    if (isExplicitAnimatedLayout || isInstantLayout) {
      connectLayoutCenterRef?.current?.(nextPos, LAYOUT_ANIMATION_DURATION);
    }
    if (layoutRequestModeRef) layoutRequestModeRef.current = null;
    commitElkBeforeReveal(stamped);
    revealEdgesAfterExplicitTidyIfNeeded();
    finishExplicitHold();
    return;
  }

  if (manualDrift && !isExplicitAnimatedLayout) {
    // ELK still needs to update cached routes, but it must not visually pull
    // manually placed nodes back into auto-layout. React/handles already use
    // these manual coordinates; force the DOM to match them in case a prior
    // direct animation wrote a stale transform.
    snapDomNodesToPositions(manualPositions);
    if (layoutRequestModeRef) layoutRequestModeRef.current = null;
    commitElk(stamped);
    return;
  }

  const layoutAnimMs = resolveLayoutAnimMs({
    isInstantLayout,
    isExplicitAnimatedLayout,
    held,
    nextPos,
    tasks: layoutTasks,
  });

  if (layoutAnimMs <= 0) {
    if (isExplicitAnimatedLayout || isInstantLayout) {
      connectLayoutCenterRef?.current?.(nextPos, 0);
    }
    snapDomNodesToPositions(nextPos);
    if (layoutRequestModeRef) layoutRequestModeRef.current = null;
    if (isExplicitAnimatedLayout) {
      flushSync(() => {
        clearCustomPositionsBeforeElkApplyRef?.current?.(nextPos);
        setElkLayoutResult(stamped);
      });
    } else {
      clearCustomPositionsBeforeElkApplyRef?.current?.(nextPos);
      commitElkBeforeReveal(stamped);
    }
    revealEdgesAfterExplicitTidyIfNeeded();
    finishExplicitHold();
    return;
  }

  // Direct-DOM animation: bypass React so the browser interpolates old→new.
  // Hide edges before FLIP for implicit animated layouts; explicit Tidy already hid
  // edges in triggerLayout. Reveal runs only after ELK is committed and inline
  // transform is cleared (same timeout tick — avoids parallel timers racing).
  if (!isExplicitAnimatedLayout) {
    startLayoutEdgeHiddenPhase();
  }
  if (isExplicitAnimatedLayout || isInstantLayout) {
    connectLayoutCenterRef?.current?.(nextPos, layoutAnimMs);
  }
  animateNodesToPositions(
    nextPos,
    () => {
      if (isCancelled()) return false;
      if (layoutVersionWhenStarted !== layoutVersionRef.current) return false;
      // Same tick as --node-tx write: drop stale customs AND commit ELK
      // so React does not paint old x/y between FLIP end and persist.
      if (layoutRequestModeRef) layoutRequestModeRef.current = null;
      flushSync(() => {
        clearCustomPositionsBeforeElkApplyRef?.current?.(nextPos);
        setElkLayoutResult(stamped);
      });
    },
    layoutAnimMs,
    () => {
      if (layoutVersionWhenStarted === layoutVersionRef.current) {
        finishExplicitHold();
      }
      scheduleLayoutEdgeRevealAfter(0);
    },
  );
}
